/*
 * FX service using backend-provided EUR-based daily rates
 *
 * - Fetches from /api/v1/fx/current (served by backend, stored in DB)
 * - Caches the latest rates in localStorage (default TTL 24h)
 * - Provides convert(amount, from, to, date?) and getRates(base, symbols?)
 *   for compatibility; the optional date argument is ignored.
 */

export type FxRates = Record<string, number>; // Map currency -> units per EUR

interface EcbDailyCache {
  date: string;           // YYYY-MM-DD
  rates: FxRates;         // currency -> units per EUR, includes EUR:1
  timestamp: number;      // ms since epoch when cached
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const CACHE_KEY = 'fx:daily:rates';

class FxService {
  private readonly ttlMs: number;
  private memoryCache: EcbDailyCache | null = null;

  constructor(ttlMs: number = ONE_DAY_MS) {
    this.ttlMs = ttlMs;
    // Eagerly prefetch current daily rates once at startup to populate cache
    this.ensureRates().catch(e => console.warn('[FX] Prefetch failed', e));
  }

  private now(): number { return Date.now(); }

  private readCache(): EcbDailyCache | null {
    if (this.memoryCache && (this.now() - this.memoryCache.timestamp) <= this.ttlMs) {
      return this.memoryCache;
    }
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as EcbDailyCache;
      if (!parsed || !parsed.rates || !parsed.date || !parsed.timestamp) return null;
      if (this.now() - parsed.timestamp > this.ttlMs) return null;
      this.memoryCache = parsed;
      return parsed;
    } catch {
      return null;
    }
  }

  private writeCache(entry: EcbDailyCache): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
      this.memoryCache = entry;
    } catch {
      // ignore quota errors
    }
  }

  // Fetch daily rates from backend API and cache
  private async fetchDaily(): Promise<EcbDailyCache> {
    const res = await fetch('/api/v1/fx/current', { cache: 'no-cache' });
    if (!res.ok) {
      throw new Error(`FX rates request failed: ${res.status}`);
    }
    const data = await res.json();
    const date = (data.date || '').slice(0, 10);
    const rates: FxRates = { ...(data.rates || {}), EUR: 1 };
    const entry: EcbDailyCache = { date, rates, timestamp: this.now() };
    this.writeCache(entry);
    return entry;
  }

  private async ensureRates(): Promise<EcbDailyCache> {
    const cached = this.readCache();
    if (cached) return cached;
    return this.fetchDaily();
  }

  // Ensure that all required currency codes are present; refresh once if any missing.
  private async ensureCurrencies(entry: EcbDailyCache, currencies: string[]): Promise<EcbDailyCache> {
    if (!currencies || currencies.length === 0) return entry;
    const needed = new Set(currencies.map((c: string) => (c || 'EUR').toUpperCase()));
    needed.delete('EUR');
    for (const code of needed) {
      if (!entry.rates[code]) {
        try {
          return await this.fetchDaily();
        } catch (e) {
          console.warn('[FX] Failed to refresh FX rates for missing currencies', e);
          return entry;
        }
      }
    }
    return entry;
  }

  // Compute pair rate using EUR as pivot. Returns null if any required leg is missing.
  private computePairRate(entry: EcbDailyCache, from: string, to: string): number | null {
    const a = (from || 'EUR').toUpperCase();
    const b = (to || 'EUR').toUpperCase();

    if (a === b) return 1;
    if (a === 'EUR') {
      const eurToB = entry.rates[b];
      return eurToB ?? null;
    }
    if (b === 'EUR') {
      const eurToA = entry.rates[a];
      return eurToA ? 1 / eurToA : null;
    }
    const eurToA = entry.rates[a];
    const eurToB = entry.rates[b];
    if (!eurToA || !eurToB) return null;
    return eurToB / eurToA;
  }

  // Get a map of rates for a given base currency (compute via EUR pivot)
  async getRates(base: string, symbols?: string[]): Promise<FxRates> {
    let entry = await this.ensureRates();
    const baseCcy = (base || 'EUR').toUpperCase();

    const list = symbols && symbols.length > 0 ? symbols.map(s => (s || 'EUR').toUpperCase()) : undefined;

    // Shortcut for EUR base
    if (baseCcy === 'EUR') {
      if (!list || list.length === 0) return { ...entry.rates };
      // Ensure requested symbols exist; refresh once if any are missing
      entry = await this.ensureCurrencies(entry, list);
      const out: FxRates = {};
      for (const k of list) {
        if (k === 'EUR') out[k] = 1;
        else if (entry.rates[k]) out[k] = entry.rates[k];
      }
      return out;
    }

    // For non-EUR base: rate(base->X) = rate(EUR->X) / rate(EUR->base)
    const required = list ? [baseCcy, ...list] : [baseCcy];
    entry = await this.ensureCurrencies(entry, required);

    const eurToBase = entry.rates[baseCcy];
    if (!eurToBase) {
      console.warn(`[FX] Missing EUR→${baseCcy} reference; returning identity for base only`);
      const out: FxRates = {};
      const only = list && list.length > 0 ? list : [baseCcy];
      for (const k of only) {
        if (k === baseCcy) out[k] = 1;
      }
      return out;
    }

    const out: FxRates = {};
    const targets = list && list.length > 0 ? list : Object.keys(entry.rates);
    for (const k of targets) {
      if (k === baseCcy) { out[k] = 1; continue; }
      if (k === 'EUR') { out[k] = 1 / eurToBase; continue; }
      const eurToSym = entry.rates[k];
      if (eurToSym) out[k] = eurToSym / eurToBase;
    }
    return out;
  }

  // Compute pair rate using EUR pivot. date is accepted for compatibility.
  async getPairRate(from: string, to: string, _date?: string): Promise<number> {
    let entry = await this.ensureRates();
    const needed = [from, to].map((c: string) => (c || 'EUR').toUpperCase());
    entry = await this.ensureCurrencies(entry, needed);
    const rate = this.computePairRate(entry, from, to);
    if (rate !== null) return rate;

    console.warn(`[FX] Missing rate for ${(from || 'EUR').toUpperCase()}→${(to || 'EUR').toUpperCase()}; falling back to 1`);
    return 1;
  }

  async convert(amount: number, from: string, to: string, date?: string): Promise<number> {
    if (!isFinite(amount)) return 0;
    if (from && to && from.toUpperCase() === to.toUpperCase()) return Math.round(amount * 100) / 100;
    // Date parameter is accepted for compatibility but ignored; using current daily rates
    const rate = await this.getPairRate(from, to);
    return Math.round((amount * rate) * 100) / 100;
  }
}

export const fxService = new FxService();
